import type { VoiceLiveEvent } from '../../machine/types';

export type InspectorParticipantId = 'ui' | 'browser' | 'machine' | 'backend' | 'avatar';

export interface InspectorParticipant {
  id: InspectorParticipantId;
  label: string;
}

export const INSPECTOR_PARTICIPANTS: InspectorParticipant[] = [
  { id: 'ui', label: 'User / UI' },
  { id: 'browser', label: 'Browser / Audio' },
  { id: 'machine', label: 'Voice Live Machine' },
  { id: 'backend', label: 'Backend WebSocket' },
  { id: 'avatar', label: 'Avatar Peer' },
];

export interface InspectorInteraction {
  source: InspectorParticipantId;
  target: InspectorParticipantId;
}

export const isXStateFrameworkEvent = (eventType: string) => eventType.startsWith('xstate.');

export const classifyVoiceLiveInteraction = (
  eventType: VoiceLiveEvent['type'],
): InspectorInteraction => {
  switch (eventType) {
    case 'START':
    case 'STOP':
    case 'MANUAL_RETRY':
    case 'REATTEMPT':
    case 'SWITCH_TO_AUDIO':
    case 'SET_ERROR':
    case 'SET_VIDEO_ENABLED':
      return { source: 'ui', target: 'machine' };
    case 'ONLINE':
    case 'OFFLINE':
    case 'MIC_CAPTURE_FAILED':
      return { source: 'browser', target: 'machine' };
    case 'WS_OPEN':
    case 'WS_CLOSE':
    case 'WS_ERROR':
    case 'SESSION_UPDATED':
    case 'ICE_SERVERS':
    case 'FIELDONE_SESSION_CREATED':
      return { source: 'backend', target: 'machine' };
    case 'AVATAR_ACTIVE':
    case 'AVATAR_FAILED':
      return { source: 'avatar', target: 'machine' };
    case 'RECONNECT_SESSION':
      return { source: 'machine', target: 'machine' };
    default:
      return assertNever(eventType);
  }
};

export const classifyInspectorInteraction = (eventType: string): InspectorInteraction => {
  if (isXStateFrameworkEvent(eventType)) return { source: 'machine', target: 'machine' };

  try {
    return classifyVoiceLiveInteraction(eventType as VoiceLiveEvent['type']);
  } catch {
    return { source: 'machine', target: 'machine' };
  }
};

const assertNever = (value: never): never => {
  throw new Error(`Unclassified Voice Live event: ${value}`);
};
